import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import type { GamePos, MapExtract, Pin, Stroke, StrokePart, Tool } from './types'
import { hitsStroke, round4, splitStroke, toParts, toPathData } from './doodle'
import {
  boundsFor,
  crsFor,
  flatPos,
  frameFor,
  gamePos,
  loadMapSvg,
  setVisibleLayers,
  svgBoundsFor,
  visibleOnLayers,
  type TarkovMapDef,
} from './tarkovMaps'

/**
 * tarkov.dev の SVG マップを敷いて、ピン・タスクの場所・落書きを重ねる。
 *
 * 座標系はゲーム内座標そのもの。マップごとの回転・縮尺は `crsFor` が CRS として持つので、
 * ここでは `gamePos({x,y,z})` を Leaflet に渡すだけでよい。
 *
 * **CRS はマップごとに違い、Leaflet は後から差し替えられない。**
 * そのため呼び出し側は `<MapView key={map.normalizedName} …>` で作り直すこと。
 *
 * SVG は CC BY-NC-SA 4.0。無改変で敷き、ラベルもピンも上に重ねるだけにしている
 * （ファイルを書き換えると SA 条項が発動する）。詳しくは NOTICE.md。
 */

/** マップに描くタスクの場所。番号は登録一覧の通し番号 */
export type LocationMark = {
  /** 場所ごとに一意 */
  id: string
  /** どのタスクの場所か。**同じタスクの近すぎる場所は番号を1つにまとめる**ために使う */
  taskId: string
  no: number
  taskName: string
  /** 依頼元の商人。番号に触れたときに出す */
  trader: string
  objectives: string[]
  /** この場所を指す目標が**すべて任意**か。番号バッジの色を変える */
  optional: boolean
  /** カッパコンテナに必要なタスクか。ポップアップに印を出す */
  kappa: boolean
  /** 中心。番号バッジを置く位置 */
  position: GamePos
  /** 多角形。空なら中心だけ描く */
  outline: GamePos[]
  /** 高さの範囲。フロアの出し分けに使う */
  top: number | null
  bottom: number | null
}

type Props = {
  map: TarkovMapDef
  /** 脱出地点。サーバから来るので、届く前は空でよい */
  extracts: MapExtract[]
  /** 地上に重ねて表示するフロアの svgLayer id */
  visibleLayers: string[]
  pins: Pin[]
  /** 登録タスクの場所。表示中のマップのぶんだけ渡ってくる */
  locations: LocationMark[]
  /** 落書き。描きかけも含めて渡ってくる */
  strokes: Stroke[]
  selectedId: string | null
  /** 今どれで触っているか。ペンと消しゴムはマップのドラッグを奪う */
  tool: Tool
  doodleVisible: boolean
  /** 通り名・建物名の表示 */
  labelsVisible: boolean
  /** 脱出地点の表示 */
  extractsVisible: boolean
  /** タスク名を場所の番号の上に出す */
  taskNamesVisible: boolean
  /** 新しく置くピンの高さ。選択中のフロアで決まる */
  pinHeight: number
  eraser: { size: number; mode: 'stroke' | 'area' }
  onSelect: (id: string | null) => void
  onAdd: (pos: GamePos) => void
  onMove: (id: string, pos: GamePos) => void
  onDrawBegin: (id: string, pts: number[]) => void
  onDrawPoints: (id: string, pts: number[]) => void
  onDrawEnd: (id: string) => void
  onErase: (ids: string[]) => void
  onSplit: (id: string, parts: StrokePart[]) => void
}

const escapeHtml = (s: string) =>
  s.replace(/[<>&"]/g, (c) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;' })[c]!)

function pinIconHtml(pin: Pin, selected: boolean): string {
  const label = pin.label ? `<span class="pin-label">${escapeHtml(pin.label)}</span>` : ''
  return `<div class="pin${selected ? ' pin--selected' : ''}">
    <svg class="pin-glyph" viewBox="0 0 24 32" width="24" height="32" aria-hidden="true">
      <path d="M12 0C5.4 0 0 5.4 0 12c0 8.4 12 20 12 20s12-11.6 12-20C24 5.4 18.6 0 12 0z" fill="${pin.color}"/>
      <circle cx="12" cy="12" r="4.5" fill="rgba(0,0,0,.55)"/>
    </svg>
    ${label}
  </div>`
}

const SVG_NS = 'http://www.w3.org/2000/svg'

/** 番号に触れたとき・押したときに出す中身。タスク名・商人・その場所の目標 */
function locationPopupHtml(loc: LocationMark): string {
  const objectives = loc.objectives.length
    ? `<ul class="loc-popup__objectives">${loc.objectives.map((o) => `<li>${escapeHtml(o)}</li>`).join('')}</ul>`
    : ''
  const trader = loc.trader ? `<div class="loc-popup__meta">${escapeHtml(loc.trader)}</div>` : ''
  // カッパコンテナに要るタスクは、タスク名の後ろに印を出す
  const kappa = loc.kappa ? `<span class="loc-popup__kappa" title="Kappa">Κ</span>` : ''
  const no = `loc-popup__no${loc.optional ? ' loc-popup__no--optional' : ''}`
  return `<div class="loc-popup">
    <div class="loc-popup__head"><span class="${no}">${loc.no}</span>${escapeHtml(loc.taskName)}${kappa}</div>
    ${trader}
    ${objectives}
  </div>`
}

/** 落書きのオーバーレイを載せる専用ペイン。場所の図形より前、ピンより後ろ */
const DOODLE_PANE = 'doodle'
/** 通り名・建物名と脱出地点のラベル。落書きより後ろ、SVG より前 */
const LABEL_PANE = 'maplabel'
/** 1本の点数の上限。到達したら継ぎ目なく次の線へ引き継ぐ */
const MAX_STROKE_POINTS = 2000
/** 点の間引き。直前の点から画面上これ未満なら捨てる */
const MIN_STEP_PX = 2
/**
 * 引いた状態で小さいラベルまで出すと文字が重なって読めない。
 * 全体表示からこの段だけ寄せたら、小さいものも出す。
 */
const LABEL_DETAIL_ZOOM = 1.2

/**
 * 番号バッジ（22px）をまとめる距離。**画面の px で見る。**
 * 同じタスクの場所がこれより近く並ぶと数字が重なって読めないので、1つにまとめる。
 * ゲーム内の距離で決めないのは、同じ 20m でも Factory と Streets では見え方が違うため。
 */
const BADGE_MERGE_PX = 26
/** 引いた状態でも出すラベルの大きさ（maps.json の size。通り名 80 / 建物 90） */
const LABEL_MAJOR_SIZE = 80
/**
 * 全体表示（fit）より何段引けるか。1段で辺の長さが半分、面積は 1/4。
 * → doc/realtime-pins/master.md「全体表示より引けるようにする」
 */
const ZOOM_BELOW_FIT = 1

export function MapView({
  map,
  extracts,
  visibleLayers,
  pins,
  locations,
  strokes,
  selectedId,
  tool,
  doodleVisible,
  labelsVisible,
  extractsVisible,
  taskNamesVisible,
  pinHeight,
  eraser,
  onSelect,
  onAdd,
  onMove,
  onDrawBegin,
  onDrawPoints,
  onDrawEnd,
  onErase,
  onSplit,
}: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<L.Map | null>(null)
  const markersRef = useRef(new Map<string, L.Marker>())
  /** 場所の図形と番号。ピンと違って利用者は触らないので、まとめて作り直す */
  const locLayersRef = useRef<L.Layer[]>([])
  /** 通り名・建物名 */
  const labelLayerRef = useRef<L.LayerGroup | null>(null)
  /** 脱出地点 */
  const extractLayerRef = useRef<L.LayerGroup | null>(null)
  /** 敷いた SVG。フロアの切り替えは、これに display を当てるだけで済む */
  const svgRef = useRef<SVGSVGElement | null>(null)
  const svgOverlayRef = useRef<L.SVGOverlay | null>(null)
  /** 最後に適用した「全体が入る倍率」。今が全体表示かの判定に使う */
  const fitZoomRef = useRef<number | null>(null)
  /** ドラッグ中のピン。サーバからの更新で位置を奪わないためのガード */
  const draggingRef = useRef<string | null>(null)
  /** 落書きを描く SVG。中に線ごとの <path> を並べる */
  const doodleSvgRef = useRef<SVGSVGElement | null>(null)
  /** 線ごとの <path> と、前回描いたときの状態。差分更新のため */
  const pathsRef = useRef(new Map<string, { el: SVGPathElement; len: number; color: string; width: number }>())
  /** 描いている最中の状態。null なら描いていない */
  const drawRef = useRef<{ id: string; count: number; lastPx: { x: number; y: number } } | null>(null)
  /** スペースキーを押している間だけ一時的に「操作」に戻す */
  const [spaceHeld, setSpaceHeld] = useState(false)
  /** 地図ができたら1回だけ進める。重ねるものはこれを待つ */
  const [ready, setReady] = useState(false)
  /** ラベルの間引き判定に使う。ズームで変わる */
  const [detail, setDetail] = useState(false)
  /** いまの縮尺。番号バッジが画面上で重なるかの判定に使う（→ 場所の描画） */
  const [zoom, setZoom] = useState(0)

  /** マップの範囲と、落書き用の平面座標系。マップが変わらないかぎり固定 */
  const bounds = useMemo(() => boundsFor(map), [map])
  const frame = useMemo(() => frameFor(map), [map])

  /** 今のコンテナに対する「全体が入る倍率」。非表示中（寸法0）は null */
  const fitZoom = useCallback(() => {
    const leaflet = mapRef.current
    const el = containerRef.current
    if (!leaflet || !el || !el.clientWidth || !el.clientHeight) return null
    // getBoundsZoom は minZoom で頭打ちになるので、いったん下限を外して測る
    leaflet.setMinZoom(-8)
    return leaflet.getBoundsZoom(bounds)
  }, [bounds])

  const applyFit = useCallback(() => {
    const leaflet = mapRef.current
    const z = fitZoom()
    if (!leaflet || z === null) return
    leaflet.fitBounds(bounds, { animate: false })
    // 全体表示より1段だけ引けるようにし、外へパンしすぎないよう余白付きで固定。
    // 引くとマップが画面より小さくなり周囲に余白が出る（正常）
    // → doc/realtime-pins/master.md「全体表示より引けるようにする」
    leaflet.setMinZoom(z - ZOOM_BELOW_FIT)
    leaflet.setMaxBounds(bounds.pad(0.25))
    fitZoomRef.current = z
  }, [fitZoom, bounds])

  /** 今が全体表示か。zoomSnap:0 なので丸めが入らず、倍率の一致で見分けられる */
  const atFit = useCallback(() => {
    const leaflet = mapRef.current
    const z = fitZoomRef.current
    return !!leaflet && z !== null && Math.abs(leaflet.getZoom() - z) < 1e-6
  }, [])

  // 最新のコールバックと設定を ref 経由で参照し、ハンドラを貼り直さずに済ませる
  const cb = useRef({ onSelect, onAdd, onMove, onDrawBegin, onDrawPoints, onDrawEnd, onErase, onSplit, tool, eraser, strokes, pinHeight })
  cb.current = { onSelect, onAdd, onMove, onDrawBegin, onDrawPoints, onDrawEnd, onErase, onSplit, tool, eraser, strokes, pinHeight }

  // ---- マップ生成 ----
  // CRS はマップごとに違い、Leaflet は後から差し替えられない。
  // 呼び出し側が key で作り直す前提なので、ここは実質1回だけ走る。
  useEffect(() => {
    if (!containerRef.current) return
    const leaflet = L.map(containerRef.current, {
      crs: crsFor(map),
      minZoom: -8,
      maxZoom: map.maxZoom ?? 8,
      zoomSnap: 0, // 端数を丸めない。「全体が入る倍率」を正確に出すため
      zoomDelta: 0.5,
      wheelPxPerZoomLevel: 90,
      doubleClickZoom: false,
      // SVG の作者表示は CC BY-NC-SA の BY 義務。消さないこと（NOTICE.md）
      attributionControl: true,
    })
    mapRef.current = leaflet
    leaflet.attributionControl.setPrefix('')

    // 落書きは「場所の図形(overlayPane 400)より前、ピンと番号(markerPane 600)より後ろ」に固定する
    leaflet.createPane(DOODLE_PANE)
    const doodlePane = leaflet.getPane(DOODLE_PANE)
    if (doodlePane) {
      doodlePane.style.zIndex = '450'
      doodlePane.style.pointerEvents = 'none'
    }
    // 地名は地図の一部として、落書きより下に敷く
    leaflet.createPane(LABEL_PANE)
    const labelPane = leaflet.getPane(LABEL_PANE)
    if (labelPane) {
      labelPane.style.zIndex = '440'
      labelPane.style.pointerEvents = 'none'
    }

    leaflet.on('click', (e: L.LeafletMouseEvent) => {
      if (cb.current.tool === 'pin') {
        cb.current.onAdd({ x: e.latlng.lng, y: cb.current.pinHeight, z: e.latlng.lat })
      } else {
        cb.current.onSelect(null)
      }
    })

    const onZoom = () => {
      const z = fitZoomRef.current
      setDetail(z !== null && leaflet.getZoom() >= z + LABEL_DETAIL_ZOOM)
      setZoom(leaflet.getZoom())
    }
    leaflet.on('zoomend', onZoom)

    applyFit()
    onZoom()
    setReady(true)

    return () => {
      leaflet.remove()
      mapRef.current = null
      markersRef.current.clear()
      setReady(false)
    }
    // map は key で作り直される前提なので、意図して初回だけ
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ---- SVG マップを敷く ----
  useEffect(() => {
    const leaflet = mapRef.current
    if (!leaflet || !ready) return
    let cancelled = false

    loadMapSvg(map, visibleLayers)
      .then((svg) => {
        if (cancelled || !mapRef.current) return
        svgRef.current = svg
        svgOverlayRef.current?.remove()
        svgOverlayRef.current = L.svgOverlay(svg, svgBoundsFor(map), {
          className: 'map-svg',
          // CC BY-NC-SA 4.0 の BY 義務。作者は定義から引く（決め打ちにしない）
          attribution:
            `Map by <a href="${map.authorLink}" target="_blank" rel="noreferrer">${escapeHtml(map.author)}</a>` +
            ' / <a href="http://creativecommons.org/licenses/by-nc-sa/4.0/" target="_blank" rel="noreferrer">CC BY-NC-SA 4.0</a>',
        }).addTo(leaflet)
        svgOverlayRef.current.bringToBack()
      })
      .catch((err: Error) => console.error('[map] SVG を読み込めませんでした:', err.message))

    return () => {
      cancelled = true
    }
    // visibleLayers は下の効果で当て直すので、ここでは読み込みのきっかけにしない
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, ready])

  // ---- フロアの出し分け ----
  // SVG には全フロアが重なって入っている。読み直さず display を当て替えるだけで済む。
  useEffect(() => {
    if (svgRef.current) setVisibleLayers(svgRef.current, map, visibleLayers)
  }, [map, visibleLayers, ready])

  // ---- コンテナのサイズ変更に追従 ----
  // タブ切り替え（0⇄実寸）・サイドパネルの開閉と幅ドラッグ・ウィンドウのリサイズ、
  // いずれもここで拾う。下限を取り直さないと、広い画面で決めた下限が狭い画面に
  // 持ち越されてマップの一部が永久に見えなくなる。
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const ro = new ResizeObserver(() => {
      const leaflet = mapRef.current
      if (!leaflet) return
      if (!el.clientWidth || !el.clientHeight) return // 非表示中
      const wasFit = atFit()
      leaflet.invalidateSize({ animate: false })
      if (wasFit) {
        applyFit()
        return
      }
      const z = fitZoom()
      if (z === null) return
      const floor = z - ZOOM_BELOW_FIT
      leaflet.setMinZoom(floor)
      fitZoomRef.current = z
      // 自分で引いていたぶんは残す。下限を割ったときだけ下限へ戻す
      if (leaflet.getZoom() < floor) leaflet.setZoom(floor, { animate: false })
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [fitZoom, applyFit, atFit])

  // ---- ピンの同期 ----
  useEffect(() => {
    const leaflet = mapRef.current
    if (!leaflet || !ready) return

    const markers = markersRef.current
    const seen = new Set<string>()

    for (const pin of pins) {
      seen.add(pin.id)
      const latlng = L.latLng(gamePos(pin.pos))
      const icon = L.divIcon({
        html: pinIconHtml(pin, pin.id === selectedId),
        className: 'pin-icon',
        iconSize: [24, 32],
        iconAnchor: [12, 32],
      })

      const existing = markers.get(pin.id)
      if (existing) {
        if (draggingRef.current !== pin.id) existing.setLatLng(latlng)
        existing.setIcon(icon)
        continue
      }

      const marker = L.marker(latlng, { icon, draggable: true, autoPan: true, riseOnHover: true }).addTo(leaflet)
      const id = pin.id
      marker.on('click', (e) => {
        L.DomEvent.stopPropagation(e)
        cb.current.onSelect(id)
      })
      marker.on('dragstart', () => {
        draggingRef.current = id
        cb.current.onSelect(id)
      })
      marker.on('dragend', (e) => {
        draggingRef.current = null
        const p = (e.target as L.Marker).getLatLng()
        // 高さは置いたときのものを引き継ぐ。平面だけ動かす
        const current = pins.find((x) => x.id === id)
        cb.current.onMove(id, { x: p.lng, y: current?.pos.y ?? 0, z: p.lat })
      })
      markers.set(id, marker)
    }

    for (const [id, marker] of markers) {
      if (!seen.has(id)) {
        marker.remove()
        markers.delete(id)
      }
    }
  }, [pins, selectedId, ready])

  // ---- タスクの場所 ----
  // 図形は overlayPane、番号は markerPane に乗るので、ピンが図形に隠れることはない。
  // 図形自体はクリックを取らない（利用者が動かすものではないため）。番号だけ押せる。
  //
  // 旧方式は攻略画像の赤枠を画像解析した矩形・楕円だったが、いまはゲーム内の実測値なので
  // outline をそのまま多角形で描ける。
  useEffect(() => {
    const leaflet = mapRef.current
    if (!leaflet || !ready) return

    for (const layer of locLayersRef.current) layer.remove()
    locLayersRef.current = []

    // いま出せる場所だけにする（別のフロアにあるものはここで落ちる）
    const shown = locations.filter((loc) =>
      visibleOnLayers(map, visibleLayers, loc.position, loc.top, loc.bottom),
    )

    for (const loc of shown) {
      if (loc.outline.length >= 3) {
        locLayersRef.current.push(
          L.polygon(loc.outline.map(gamePos), {
            className: 'loc-poly',
            interactive: false,
          }).addTo(leaflet),
        )
      } else {
        // outline を持たない zone。位置だけは示す
        locLayersRef.current.push(
          L.circleMarker(gamePos(loc.position), {
            radius: 14,
            className: 'loc-poly',
            interactive: false,
          }).addTo(leaflet),
        )
      }

    }

    /*
     * 番号バッジ。**同じタスクの場所が数メートル差で並んでいることがあり**
     * （`Against the Conscience` は 3m 差、`The Door` は 3m 差）、そのまま描くと
     * 同じ数字が2つ重なって出る。いまの縮尺で重なるものは1つにまとめ、
     * ポップアップにはまとめた場所の目標を全部入れる。
     *
     * まとめるのは**バッジだけ**。図形は上で1か所ずつ描いてあるので、どこが対象かは残る。
     * 縮尺が変わればまとまり方も変わるので、`zoom` を見て組み直す。
     */
    const badges: { loc: LocationMark; point: L.Point; objectives: string[]; optional: boolean }[] = []
    for (const loc of shown) {
      const point = leaflet.project(L.latLng(gamePos(loc.position)), leaflet.getZoom())
      const near = badges.find(
        (b) => b.loc.taskId === loc.taskId && b.point.distanceTo(point) < BADGE_MERGE_PX,
      )
      if (near) {
        for (const o of loc.objectives) if (!near.objectives.includes(o)) near.objectives.push(o)
        // まとめた先に必須の場所が混ざったら、そのバッジは必須として描く
        near.optional = near.optional && loc.optional
        continue
      }
      badges.push({ loc, point, objectives: [...loc.objectives], optional: loc.optional })
    }

    // 同じタスクが1つのマップに複数の目的地を持つときは、**どこにも同じ番号**を出す
    // （一覧の番号と1対1に対応させるため → doc/task-locations/master.md#番号）
    for (const badge of badges) {
      const loc = { ...badge.loc, objectives: badge.objectives, optional: badge.optional }
      // 番号の上のタスク名は `🔖 タスク名` のときだけ。番号だけでは何のタスクか分からないため
      const name = taskNamesVisible
        ? `<span class="loc-name">${escapeHtml(loc.taskName)}</span>`
        : ''

      locLayersRef.current.push(
        L.marker(gamePos(loc.position), {
          icon: L.divIcon({
            html: `${name}<span class="loc-no${loc.optional ? ' loc-no--optional' : ''}">${loc.no}</span>`,
            className: 'loc-no-icon',
            iconSize: [22, 22],
            iconAnchor: [11, 11],
          }),
          keyboard: false,
          riseOnHover: true,
        })
          // 触れただけでタスクの中身が分かるようにする（押したときのポップアップと同じ内容）
          .bindTooltip(locationPopupHtml(loc), {
            direction: 'top',
            offset: [0, -14],
            className: 'loc-tip',
          })
          .bindPopup(locationPopupHtml(loc), { className: 'loc-popup-wrap', closeButton: false })
          .addTo(leaflet),
      )
    }

    return () => {
      for (const layer of locLayersRef.current) layer.remove()
      locLayersRef.current = []
    }
  }, [locations, map, visibleLayers, taskNamesVisible, zoom, ready])

  // ---- 通り名・建物名 ----
  // SVG に文字が入っていないのは意図した設計（配布元の README）。ラベルはアプリの仕事。
  // 座標と大きさは maps.json 由来で、size は基準フォントに対する % （tarkov.dev と同じ）。
  useEffect(() => {
    const leaflet = mapRef.current
    if (!leaflet || !ready) return

    labelLayerRef.current?.remove()
    labelLayerRef.current = null
    if (!labelsVisible) return

    const group = L.layerGroup()
    for (const label of map.labels ?? []) {
      const size = label.size ?? 100
      // 引いた状態で全部出すと文字が重なる。寄せたときだけ小さいものも出す
      if (!detail && size < LABEL_MAJOR_SIZE) continue
      const position = { x: label.position[0], z: label.position[1] }
      if (!visibleOnLayers(map, visibleLayers, position, label.top, label.bottom)) continue

      const rotation = label.rotation ?? 0
      L.marker(flatPos(label.position), {
        pane: LABEL_PANE,
        interactive: false,
        keyboard: false,
        icon: L.divIcon({
          className: 'map-label-icon',
          html: `<div class="map-label" style="font-size:${size}%;transform:translate(-50%,-50%) rotate(${rotation}deg)">${escapeHtml(label.text)}</div>`,
        }),
      }).addTo(group)
    }
    group.addTo(leaflet)
    labelLayerRef.current = group

    return () => {
      group.remove()
      labelLayerRef.current = null
    }
  }, [map, visibleLayers, labelsVisible, detail, ready])

  // ---- 脱出地点 ----
  useEffect(() => {
    const leaflet = mapRef.current
    if (!leaflet || !ready) return

    extractLayerRef.current?.remove()
    extractLayerRef.current = null
    if (!extractsVisible) return

    const group = L.layerGroup()
    for (const ex of extracts) {
      if (!visibleOnLayers(map, visibleLayers, ex.position, ex.top, ex.bottom)) continue
      const faction = ex.faction === 'scav' || ex.faction === 'pmc' ? ex.faction : 'shared'

      if (ex.outline.length >= 3) {
        L.polygon(ex.outline.map(gamePos), {
          className: `extract-poly extract-poly--${faction}`,
          interactive: false,
        }).addTo(group)
      }
      L.marker(gamePos(ex.position), {
        pane: LABEL_PANE,
        interactive: false,
        keyboard: false,
        icon: L.divIcon({
          className: 'extract-label-icon',
          html: `<div class="extract-label extract-label--${faction}">${escapeHtml(ex.name)}</div>`,
        }),
      }).addTo(group)
    }
    group.addTo(leaflet)
    extractLayerRef.current = group

    return () => {
      group.remove()
      extractLayerRef.current = null
    }
  }, [extracts, map, visibleLayers, extractsVisible, ready])

  // ---- 落書き: オーバーレイ1枚 ----
  // 線ごとにレイヤを作ると重いうえ、L.Polyline は線幅が画面ピクセル固定でズームしても太らない。
  // マップ全体を覆う SVG を1枚敷き、その中に <path> を並べる。
  // viewBox はゲーム座標を投影して作った平面（frameFor）なので、回転のあるマップでも重なる。
  useEffect(() => {
    const leaflet = mapRef.current
    if (!leaflet || !ready) return

    const svg = document.createElementNS(SVG_NS, 'svg')
    svg.setAttribute('viewBox', `0 0 ${frame.width} ${frame.height}`)
    svg.setAttribute('preserveAspectRatio', 'none')
    svg.setAttribute('class', 'doodle-svg')
    const overlay = L.svgOverlay(svg, bounds, { pane: DOODLE_PANE, interactive: false }).addTo(leaflet)
    doodleSvgRef.current = svg
    pathsRef.current.clear()

    return () => {
      overlay.remove()
      doodleSvgRef.current = null
      pathsRef.current.clear()
    }
  }, [frame, bounds, ready])

  // ---- 落書き: 線の差分更新 ----
  // 描いている最中は 60ms ごとに点が増える。毎回作り直すと他人の線まで再生成されるので、
  // <path> は id ごとに保持して、変わったものの d だけ書き換える。
  useEffect(() => {
    const svg = doodleSvgRef.current
    if (!svg) return

    svg.style.display = doodleVisible ? '' : 'none'
    if (!doodleVisible) return

    const paths = pathsRef.current
    const seen = new Set<string>()

    for (const stroke of strokes) {
      seen.add(stroke.id)
      const prev = paths.get(stroke.id)
      if (prev) {
        if (prev.len !== stroke.pts.length) {
          prev.el.setAttribute('d', toPathData(stroke.pts, frame.width, frame.height))
          prev.len = stroke.pts.length
        }
        if (prev.color !== stroke.color) {
          prev.el.setAttribute('stroke', stroke.color)
          prev.color = stroke.color
        }
        if (prev.width !== stroke.width) {
          prev.el.setAttribute('stroke-width', String(stroke.width))
          prev.width = stroke.width
        }
        continue
      }

      const el = document.createElementNS(SVG_NS, 'path')
      el.setAttribute('d', toPathData(stroke.pts, frame.width, frame.height))
      el.setAttribute('stroke', stroke.color)
      el.setAttribute('stroke-width', String(stroke.width))
      el.setAttribute('fill', 'none')
      el.setAttribute('stroke-linecap', 'round')
      el.setAttribute('stroke-linejoin', 'round')
      svg.appendChild(el)
      paths.set(stroke.id, { el, len: stroke.pts.length, color: stroke.color, width: stroke.width })
    }

    for (const [id, rec] of paths) {
      if (!seen.has(id)) {
        rec.el.remove()
        paths.delete(id)
      }
    }
  }, [strokes, doodleVisible, frame, ready])

  // ---- ペン / 消しゴムの間はマップのドラッグを切る ----
  // スペースを押している間だけ戻す（描き味を切らずにマップを動かせるように）。
  const drawing = (tool === 'pen' || tool === 'eraser') && !spaceHeld
  useEffect(() => {
    const leaflet = mapRef.current
    if (!leaflet) return
    // tap はタッチ端末でだけ生えるハンドラで型定義に無い
    const tap = (leaflet as L.Map & { tap?: L.Handler }).tap
    if (drawing) {
      leaflet.dragging.disable()
      tap?.disable()
    } else {
      leaflet.dragging.enable()
      tap?.enable()
    }
  }, [drawing])

  // ---- スペースキーで一時的に「操作」へ ----
  useEffect(() => {
    if (tool !== 'pen' && tool !== 'eraser') return
    const isTyping = (t: EventTarget | null) => {
      const tag = (t as HTMLElement | null)?.tagName
      return tag === 'INPUT' || tag === 'TEXTAREA'
    }
    const down = (e: KeyboardEvent) => {
      if (e.code !== 'Space' || e.repeat || isTyping(e.target)) return
      e.preventDefault() // ページのスクロールとボタンの誤爆を止める
      setSpaceHeld(true)
    }
    const up = (e: KeyboardEvent) => {
      if (e.code !== 'Space') return
      setSpaceHeld(false)
    }
    // ウィンドウから離れた隙に離されると押しっぱなし判定が残る
    const blur = () => setSpaceHeld(false)
    window.addEventListener('keydown', down)
    window.addEventListener('keyup', up)
    window.addEventListener('blur', blur)
    return () => {
      window.removeEventListener('keydown', down)
      window.removeEventListener('keyup', up)
      window.removeEventListener('blur', blur)
      setSpaceHeld(false)
    }
  }, [tool])

  // ---- ペン / 消しゴムの入力 ----
  useEffect(() => {
    const el = containerRef.current
    if (!el || !drawing) return

    /** 画面の座標を、落書き平面の 0..1 に落とす。外へはみ出したぶんはクランプする */
    const toNorm = (e: PointerEvent) => {
      const leaflet = mapRef.current
      if (!leaflet) return null
      const p = leaflet.mouseEventToLatLng(e)
      const [fx, fy] = frame.toFrame({ x: p.lng, z: p.lat })
      const x = Math.min(1, Math.max(0, fx / frame.width))
      const y = Math.min(1, Math.max(0, fy / frame.height))
      return { x: round4(x), y: round4(y) }
    }

    /** 消しゴムを1回当てる */
    const erase = (x: number, y: number) => {
      const { eraser, strokes, onErase, onSplit } = cb.current
      if (eraser.mode === 'stroke') {
        const ids = strokes
          .filter((st) => hitsStroke(st, x, y, eraser.size, frame.width, frame.height))
          .map((st) => st.id)
        if (ids.length) onErase(ids)
        return
      }
      for (const st of strokes) {
        const parts = splitStroke(st, x, y, eraser.size, frame.width, frame.height)
        if (parts) onSplit(st.id, toParts(parts))
      }
    }

    const onDown = (e: PointerEvent) => {
      if (e.button !== 0) return // 右クリックはコンテキストメニューに渡す
      const pt = toNorm(e)
      if (!pt) return
      e.preventDefault()
      el.setPointerCapture(e.pointerId)

      if (cb.current.tool === 'eraser') {
        drawRef.current = { id: '', count: 0, lastPx: { x: e.clientX, y: e.clientY } }
        erase(pt.x, pt.y)
        return
      }

      const id = crypto.randomUUID()
      drawRef.current = { id, count: 1, lastPx: { x: e.clientX, y: e.clientY } }
      cb.current.onDrawBegin(id, [pt.x, pt.y])
    }

    const onMovePointer = (e: PointerEvent) => {
      const cur = drawRef.current
      if (!cur) return
      // 間引き。密すぎる点は通信量ばかり増えて形は変わらない
      const dx = e.clientX - cur.lastPx.x
      const dy = e.clientY - cur.lastPx.y
      if (dx * dx + dy * dy < MIN_STEP_PX * MIN_STEP_PX) return
      const pt = toNorm(e)
      if (!pt) return
      cur.lastPx = { x: e.clientX, y: e.clientY }

      if (cb.current.tool === 'eraser') {
        erase(pt.x, pt.y)
        return
      }

      if (cur.count >= MAX_STROKE_POINTS) {
        // 上限に達したら、その場で終端して同じ点から次の線へ継ぐ（描き味を切らない）
        cb.current.onDrawEnd(cur.id)
        const id = crypto.randomUUID()
        cur.id = id
        cur.count = 1
        cb.current.onDrawBegin(id, [pt.x, pt.y])
        return
      }

      cur.count += 1
      cb.current.onDrawPoints(cur.id, [pt.x, pt.y])
    }

    const onUp = (e: PointerEvent) => {
      const cur = drawRef.current
      drawRef.current = null
      if (el.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId)
      if (cur?.id) cb.current.onDrawEnd(cur.id)
    }

    el.addEventListener('pointerdown', onDown)
    el.addEventListener('pointermove', onMovePointer)
    el.addEventListener('pointerup', onUp)
    el.addEventListener('pointercancel', onUp)

    return () => {
      el.removeEventListener('pointerdown', onDown)
      el.removeEventListener('pointermove', onMovePointer)
      el.removeEventListener('pointerup', onUp)
      el.removeEventListener('pointercancel', onUp)
      // ツールを変えた・アンマウントした時点で描きかけを閉じる
      const cur = drawRef.current
      drawRef.current = null
      if (cur?.id) cb.current.onDrawEnd(cur.id)
    }
  }, [drawing, frame])

  const mode = drawing ? (tool === 'pen' ? ' map-canvas--pen' : ' map-canvas--eraser') : ''
  return <div ref={containerRef} className={`map-canvas${tool === 'pin' ? ' map-canvas--add' : ''}${mode}`} />
}
